<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, computed } from 'vue';
import { useMapStore } from '@/stores/mapStore.ts'
import type MapCoordinate from '@/types/MapCoordinate.ts'
import MapToolTipStar from '@/components/Sections/Map/Galaxy/WebGLMap/MapObjects/MapToolTip/MapToolTipStar.vue'
import MapToolTipPlanet from '@/components/Sections/Map/Galaxy/WebGLMap/MapObjects/MapToolTip/MapToolTipPlanet.vue'
import MapToolTipShips from '@/components/Sections/Map/Galaxy/WebGLMap/MapObjects/MapToolTip/MapToolTipShips.vue'
import { useGameStore } from '@/stores/gameStore.ts'
import { useMapCoordinateStore } from '@/stores/mapCoordinateStore.ts'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import MapToolTipPortal from '@/components/Sections/Map/Galaxy/WebGLMap/MapObjects/MapToolTip/MapToolTipPortal.vue'
import MapToolTipBlackHole from '@/components/Sections/Map/Galaxy/WebGLMap/MapObjects/MapToolTip/MapToolTipBlackHole.vue'
import { usePortalStore } from '@/stores/portalStore.ts'
import { useMapSettingsStore } from '@/stores/mapSettingsStore.ts'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// Define props
const props = defineProps<{
  toolTipVisible: boolean
}>();

const mouseX = ref(0);
const mouseY = ref(0);

// Data to be displayed in the tooltip
const coordinate = ref<MapCoordinate | null>(null);

// Dragging state
const isDragging = ref(false);
const dragOffset = ref({ x: 0, y: 0 });
const tooltipRef = ref<HTMLElement | null>(null);

const mapStore = useMapStore();
const gameStore = useGameStore();
const mapCoordinateStore = useMapCoordinateStore()
const portalStore = usePortalStore()
const mapSettingsStore = useMapSettingsStore()

// Store the last mouse event
let lastMouseEvent: MouseEvent | null = null;

const updateMousePosition = (event: MouseEvent) => {
  lastMouseEvent = event;
};

onMounted(() => {
  window.addEventListener('mousemove', updateMousePosition);
});

onUnmounted(() => {
  window.removeEventListener('mousemove', updateMousePosition);
});

const loadMapCoordinate = async () => {
    const data = mapCoordinateStore.getByCoordinates(mapStore.tooltipX, mapStore.tooltipY);
    //If we are on space
    if(data.mapCoordinate.type == 0) {
      //Are we dealing with only one player?
      if (Object.keys(data.mapCoordinate.ships).length == 1) {
        //If so, check if the player is the current player
        if (Object.keys(data.mapCoordinate.ships).includes(String(gameStore.player?.id))) {
          //If so, we don't want to show the tooltip
          gameStore.modalType = 'mapSelectPlayerShip'
          gameStore.modalData = {
            xPos: mapStore.tooltipX,
            yPos: mapStore.tooltipY,
          }
          coordinate.value = null;
          return;
        }
      }
    }

    coordinate.value = data.mapCoordinate;
};


// Helper to position the tooltip at current mouse location
const positionTooltipAtMouse = () => {
  // Check if we should use saved position and if we have one
  if (mapSettingsStore.rememberTooltipPosition &&
      mapStore.tooltipLastPosition.x !== null &&
      mapStore.tooltipLastPosition.y !== null) {
    // Use the saved position
    mouseX.value = mapStore.tooltipLastPosition.x;
    mouseY.value = mapStore.tooltipLastPosition.y;
  } else if (lastMouseEvent) {
    // Use mouse position for first time
    const tooltipWidth = 320; // Assume a fixed tooltip width
    const tooltipHeight = 300; // Assume a fixed tooltip height
    const padding = 10;

    let x = lastMouseEvent.clientX;
    let y = lastMouseEvent.clientY;

    // Adjust x position to avoid overflow
    if (x + tooltipWidth + padding > window.innerWidth) {
      x = window.innerWidth - tooltipWidth - padding;
    }

    // Adjust y position to avoid overflow
    if (y + tooltipHeight + padding > window.innerHeight) {
      y = window.innerHeight - tooltipHeight - padding;
    }

    mouseX.value = x;
    mouseY.value = y;
  }
};

// Watcher for toolTipVisible prop to update position when it turns true
watch(() => props.toolTipVisible, (newValue) => {
  if (newValue) {
    positionTooltipAtMouse();
  }
});

// Watcher for blackHoleTooltipVisible to update position
watch(() => mapStore.blackHoleTooltipVisible, (newValue) => {
  if (newValue) {
    positionTooltipAtMouse();
  }
});

// Add copy coordinates function
const copyCoordinates = () => {
  if (coordinate.value) {
    let prefix = '';
    let name = '';

    switch(Number(coordinate.value.type)) {
      case 1: // Star
        prefix = t('map.tooltips.star');
        name = coordinate.value.typeName || '';
        break;
      case 2: // Planet
        prefix = t('map.tooltips.planet');
        name = coordinate.value.typeName || '';
        break;
      case 4: { // Portal/Wormhole
        prefix = t('map.tooltips.wormhole');
        const portal = portalStore.findPortalById(coordinate.value.typeId);
        if (portal) {
          name = portal.name;
        }
        break;
      }
      default:
        prefix = coordinate.value.typeName || '';
    }

    const coordString = `${prefix} ${name} ${coordinate.value.xPos}x${coordinate.value.yPos}`;
    navigator.clipboard.writeText(coordString);
    gameStore.setTinySuccessToastMessage(t('map.tooltips.coordinatesCopied'));
  }
};

//title
const titleContent = computed(() => {
  // Black hole tooltip
  if (mapStore.blackHoleTooltipVisible) {
    return t('map.tooltips.blackHoleTitle');
  }
  if(Number(coordinate.value?.type) === 4 && coordinate.value?.typeId) {
    // Portal
    const portal = portalStore.findPortalById(coordinate.value.typeId);
    if (portal) {
      return t('map.tooltips.wormholeTitle', { name: portal.name });
    }
  }
  return coordinate.value?.typeName || '';
});

// Check if any tooltip should be visible
const isTooltipVisible = computed(() => {
  return (props.toolTipVisible && coordinate.value != null) || mapStore.blackHoleTooltipVisible;
});

// Load the map coordinate when the tooltip is visible
watch(() => props.toolTipVisible, () => {
  if (props.toolTipVisible) {
    loadMapCoordinate();
  }
});

// Dragging functionality
const startDrag = (event: MouseEvent | TouchEvent) => {
  if (tooltipRef.value && event.target) {
    const target = event.target as HTMLElement;
    // Only allow dragging from the drag handle (title text), not buttons
    if (target.closest('.drag-handle') && !target.closest('button')) {
      isDragging.value = true;
      
      // Get the position from either mouse or touch event
      const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
      const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;
      
      dragOffset.value = {
        x: clientX - mouseX.value,
        y: clientY - mouseY.value
      };
      event.preventDefault();
    }
  }
};

const onDrag = (event: MouseEvent | TouchEvent) => {
  if (isDragging.value) {
    // Get the position from either mouse or touch event
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;
    
    mouseX.value = clientX - dragOffset.value.x;
    mouseY.value = clientY - dragOffset.value.y;
  }
};

const endDrag = () => {
  isDragging.value = false;
  // Save the position to the store if the setting is enabled
  if (mapSettingsStore.rememberTooltipPosition) {
    mapStore.tooltipLastPosition.x = mouseX.value;
    mapStore.tooltipLastPosition.y = mouseY.value;
  }
};

// Add dragging event listeners
onMounted(() => {
  // Mouse events
  document.addEventListener('mousemove', onDrag);
  document.addEventListener('mouseup', endDrag);
  
  // Touch events for mobile
  document.addEventListener('touchmove', onDrag, { passive: false });
  document.addEventListener('touchend', endDrag);
});

onUnmounted(() => {
  // Mouse events
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', endDrag);
  
  // Touch events
  document.removeEventListener('touchmove', onDrag);
  document.removeEventListener('touchend', endDrag);
});
</script>

<template>
  <div v-show="isTooltipVisible" :style="{ left: mouseX + 'px', top: mouseY + 'px' }" class="tooltip z-20" ref="tooltipRef">
    <StandardCard :no-padding="true">
      <template #title>
        <div class="flex items-center justify-between tooltip-title">
          <span class="pr-2 flex-1 drag-handle" @mousedown="startDrag" @touchstart="startDrag">{{ titleContent }}</span>
          <button v-if="coordinate && !mapStore.blackHoleTooltipVisible" @click="copyCoordinates" class="p-1 hover:bg-slate-600 rounded mr-1 z-10" :title="t('map.tooltips.copyCoordinates')">
            <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-copy" width="16" height="16" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
              <path d="M7 7m0 2.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667z"></path>
              <path d="M4.012 16.737a2.005 2.005 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1"></path>
            </svg>
          </button>
        </div>
      </template>
      <div class="bg-slate-800 flex">
        <!-- Black Hole -->
        <div v-if="mapStore.blackHoleTooltipVisible" class="min-w-40 w-full">
          <MapToolTipBlackHole />
        </div>
        <!-- Regular coordinate tooltips -->
        <div v-else-if="Number(coordinate?.type) > 0" class="min-w-40 w-full">
          <!-- Portals -->
          <div v-if="Number(coordinate?.type) == 4 && coordinate!=null">
            <MapToolTipPortal :coordinate="coordinate"></MapToolTipPortal>
          </div>
          <!-- Planets -->
          <div v-if="Number(coordinate?.type) == 2 && coordinate!=null">
            <MapToolTipPlanet :coordinate="coordinate"></MapToolTipPlanet>
          </div>
          <!-- Stars -->
          <div v-else-if="Number(coordinate?.type) == 1 && coordinate!=null">
            <MapToolTipStar :coordinate="coordinate"></MapToolTipStar>
          </div>
        </div>
        <div v-if="!mapStore.blackHoleTooltipVisible && coordinate?.ships && Object.keys(coordinate?.ships).length>0">
          <MapToolTipShips :coordinate="coordinate"></MapToolTipShips>
        </div>
      </div>
    </StandardCard>
  </div>
</template>

<style scoped lang="scss">
.tooltip {
  position: absolute;
  user-select: none;
}

.drag-handle {
  cursor: move;
  
  &:active {
    cursor: grabbing;
  }
}

.tooltip-title button {
  cursor: pointer;
  position: relative;
}
</style>
